import { FC, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigationData } from '../modules/NavigationProvider';
import { useAuth } from '../modules/auth/AuthContext';
import { updateProfile } from '../modules/auth/_requests';

const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
];

const ProfilePage: FC = () => {
  const { t, i18n } = useTranslation('profile');
  const { setPath, setButtonText, setHeaderText } = useNavigationData();
  const { currentUser } = useAuth();

  useLayoutEffect(() => {
    setPath('/menu');
    setButtonText(t('home'));
    setHeaderText(t('title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileSchema = Yup.object().shape({
    name: Yup.string().required(t('name.required')),
    phone: Yup.string()
      .matches(/^9[0-9]{8}|(00[0-9]{8,18})$/, t('phone.invalid'))
      .nullable(),
    language: Yup.string(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: currentUser?.name ?? '',
      phone: currentUser?.phone ?? '',
      language: currentUser?.language ?? 'pt',
    },
    validationSchema: profileSchema,
    validateOnChange: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        await toast.promise(updateProfile(values), {
          pending: t('message.pending'),
          success: t('message.success'),
          error: t('message.error'),
        });
      } catch (ex) {
        console.error(ex);
      } finally {
        setSubmitting(false);

        if (values.language !== i18n.language)
          i18n.changeLanguage(values.language ?? 'en');
      }
    },
  });

  return (
    <div className="d-flex justify-content-center align-items-center h-100 mw-250px mt-5">
      <Form onSubmit={formik.handleSubmit} style={{ minWidth: '300px' }}>
        <Form.Group className="mb-3">
          <Form.Label className="text-uppercase">{t('name.label')}</Form.Label>
          <Form.Control
            type="text"
            {...formik.getFieldProps('name')}
            placeholder={t('name.placeholder')}
            autoComplete="name"
            aria-autocomplete="none"
            isInvalid={formik.touched.name && formik.errors.name !== undefined}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.name}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-uppercase">{t('phone.label')}</Form.Label>
          <Form.Control
            type="text"
            {...formik.getFieldProps('phone')}
            placeholder={t('phone.placeholder')}
            aria-autocomplete="none"
            isInvalid={
              formik.touched.phone && formik.errors.phone !== undefined
            }
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.phone}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-uppercase">
            {t('language.label')}
          </Form.Label>
          <Select
            classNamePrefix="react-select"
            theme={(theme) => ({ ...theme, borderRadius: 0 })}
            options={languageOptions}
            placeholder={t('language.placeholder')}
            onChange={(e) => {
              formik.setFieldValue('language', e?.value);
            }}
            value={languageOptions.find(
              (x) => x.value === formik.values.language
            )}
          />
        </Form.Group>

        <Button
          variant="primary"
          type="submit"
          disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
        >
          {formik.isSubmitting ? (
            <span className="indicator-progress">
              {t('button.wait') + ' '}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          ) : (
            <span className="indicator-label">{t('button.save')}</span>
          )}
        </Button>
      </Form>
    </div>
  );
};

export default ProfilePage;
